import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import useSimilarMovies from "../../hooks/useSimilarMovies";
import MovieList from "../browse/MovieList";
import ShowMovieVideo from "./ShowMovieVideo";
import { useParams, useNavigate } from "react-router-dom";
import Header from "../layout/Header";
import { RootState } from "../../store";
import { motion } from "framer-motion";
import { ChevronLeft, Play, Plus, ThumbsUp } from "lucide-react";
import Footer from "../layout/Footer";

const ShowMovie = () => {
  const { movieId } = useParams<{ movieId: string }>();
  const navigate = useNavigate();
  const movies = useSelector((store: RootState) => store.movies);

  useSimilarMovies(movieId);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [movieId]);

  if (!movieId) return null;

  return (
    <div className="bg-black min-h-screen text-white selection:bg-red-600">
      <Header />

      <main>
        {/* Cinematic Video Section */}
        <section className="relative w-full aspect-video md:h-[85vh] overflow-hidden bg-black shadow-2xl">
          <ShowMovieVideo movieId={movieId} />

          {/* Back Button */}
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            onClick={() => navigate(-1)}
            className="absolute top-24 left-6 md:left-12 z-30 flex items-center space-x-2 bg-black/40 backdrop-blur-md px-4 py-2 rounded-full border border-white/10 hover:bg-white/20 transition-all text-sm font-medium"
          >
            <ChevronLeft size={20} />
            <span>Go Back</span>
          </motion.button>

          <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent pointer-events-none"></div>
        </section>

        {/* Content Section with Deep Background */}
        <div className="bg-gradient-to-b from-transparent via-[#0a0a0a] to-black">
          <div className="px-6 md:px-12 -mt-20 md:-mt-40 relative z-20 pb-20">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex flex-col md:flex-row md:items-start md:space-x-12"
            >
              {/* Info and Actions */}
              <div className="flex-1 space-y-8">
                <div className="flex items-center space-x-4">
                  <button className="flex items-center space-x-2 bg-white text-black px-8 py-3 rounded-lg font-black text-lg hover:bg-white/80 transition-all transform hover:scale-105 active:scale-95">
                    <Play size={24} fill="black" />
                    <span>Watch Now</span>
                  </button>
                  <div className="flex items-center space-x-3">
                    <button className="p-3 rounded-full border-2 border-zinc-600 hover:border-white transition-all hover:scale-110 active:scale-90"><Plus size={24} /></button>
                    <button className="p-3 rounded-full border-2 border-zinc-600 hover:border-white transition-all hover:scale-110 active:scale-90"><ThumbsUp size={24} /></button>
                  </div>
                </div>

                <div className="flex items-center space-x-3 text-sm font-bold text-zinc-400">
                  <span className="text-green-500">98% Match</span>
                  <span className="px-2 py-0.5 border border-zinc-700 rounded text-[10px]">2024</span>
                  <span className="px-2 py-0.5 border border-zinc-700 rounded text-[10px]">U/A 13+</span>
                  <span>2h 35m</span>
                  <span className="px-1.5 py-0.5 border border-zinc-700 rounded-[3px] text-[10px] font-black text-zinc-300">4K HDR</span>
                </div>

                <div className="space-y-4 max-w-3xl">
                  <p className="text-lg md:text-xl text-zinc-200 leading-relaxed font-semibold italic">
                    "Experience this breathtaking cinematic journey. Immerse yourself in a world of high-stakes drama and visual excellence."
                  </p>
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm text-zinc-400 leading-relaxed">
                      <span className="text-zinc-500 font-bold uppercase tracking-wider text-[11px] mr-2">Starring:</span>
                      <span className="text-zinc-300">Brad Pitt, Margot Robbie, Diego Calva</span>
                    </p>
                    <p className="text-sm text-zinc-400 leading-relaxed">
                      <span className="text-zinc-500 font-bold uppercase tracking-wider text-[11px] mr-2">Genres:</span>
                      <span className="text-zinc-300">Emotional • Exciting • Period Piece</span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Right Side Info Box */}
              <div className="hidden md:block w-84 space-y-6">
                <div className="p-8 bg-zinc-900/40 rounded-3xl border border-white/5 backdrop-blur-3xl shadow-2xl">
                  <h3 className="text-xs font-black text-zinc-500 mb-6 tracking-[0.2em] uppercase">Audio & Subtitles</h3>
                  <div className="space-y-5">
                    <div className="flex flex-col space-y-1">
                      <span className="text-zinc-500 text-[10px] font-bold uppercase">Audio</span>
                      <span className="text-zinc-200 text-sm font-semibold">English [Original], Hindi (5.1)</span>
                    </div>
                    <div className="flex flex-col space-y-1">
                      <span className="text-zinc-500 text-[10px] font-bold uppercase">Subtitles</span>
                      <span className="text-zinc-200 text-sm font-semibold">English, Hindi, Spanish</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Similar Movies Section */}
            <div className="mt-24 space-y-8">
              {movies.similarMovies && (
                <motion.div
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8 }}
                >
                  <MovieList title={"Because You Watched This"} movies={movies.similarMovies} />
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ShowMovie;
